import classNames from "classnames";
import { Modal, ModalBuilder } from "@redriver/cinnamon";
import { Icon } from "semantic-ui-react";
import React, { useMemo } from "react";
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import "@cyntler/react-doc-viewer/dist/index.css";
import createImageDocRenderer from "./ImageDocRenderer";
import { getFileExtension, stripFileExtension } from "modules/helpers";

// The viewer's own fallback offers a download link straight off the blob url, which would
// bypass the audited download endpoint.
const NoPreview = ({ fileName }) => (
  <div className="no-preview">
    No preview is available for {fileName || "this file"}.
  </div>
);

const viewerConfig = {
  header: { disableHeader: true },
  noRenderer: { overrideComponent: NoPreview },
};

// The viewer reloads its document whenever the identity of documents or config changes
const PreviewedDocument = ({ file, pluginRenderers }) => {
  const documents = useMemo(
    () => [
      {
        uri: file.url,
        fileName: file.name,
        fileType: getFileExtension(file.name),
      },
    ],
    [file]
  );

  return (
    <DocViewer
      className="document-viewer"
      pluginRenderers={pluginRenderers}
      documents={documents}
      prefetchMethod="GET"
      config={viewerConfig}
    />
  );
};

const DocumentFilePreview = ({
  className,
  open,
  onClose,
  fileId,
  loadAction,
  onExpand,
  closeOnEscape = true,
  renderActions,
}) => {
  const pluginRenderers = useMemo(
    () => [createImageDocRenderer(onExpand), ...DocViewerRenderers],
    [onExpand]
  );

  return (
    <ModalBuilder
      visible={open}
      loadAction={loadAction}
      loadParams={fileId}
      onCancelled={onClose}
      onSubmitted={onClose}
      renderModal={(modalProps, file) => (
        <Modal.Information
          {...modalProps}
          size="large"
          className="document-preview-modal"
          onClose={onClose}
          closeOnEscape={closeOnEscape}
          header={
            <div className="document-preview-header">
              <span className="file-name">
                {stripFileExtension(file?.name)}
              </span>
              {!!file && renderActions?.(file)}
              <Icon name="close" link onClick={onClose} />
            </div>
          }
          submitLabel="Close"
        >
          <div className={classNames("document-viewer-modal", className)}>
            {!!file?.url && (
              <PreviewedDocument
                file={file}
                pluginRenderers={pluginRenderers}
              />
            )}
          </div>
        </Modal.Information>
      )}
    />
  );
};

export default DocumentFilePreview;
